import { useCallback, useMemo, useState } from 'react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  getParentValues,
  MAX_HIERARCHY_DEPTH,
  parseHierarchyOptions,
} from '~/client/src/shared/utils/hierarchyTree'
import { toast } from '~/client/src/shared/utils/toast'

import {
  makeUniquePropertyName,
  normalizePropertyName,
} from '../../propertyName'
import type {
  HierarchyConfiguration,
  HierarchyNameDraftValidation,
  HierarchyOption,
} from './HierarchyPropertyConfig.types'
import {
  createUniqueHierarchyOptionValue,
  deleteHierarchyOptions,
  flattenHierarchyRows,
  indentHierarchyOptions,
  isBlockedNestingConversion,
  optionMatchesQuery,
  otherOptionValues,
  outdentHierarchyOptions,
  parseBulkHierarchyOptions,
  pruneValuesFromSet,
  renameValueInSet,
  changesHierarchyParents,
  reorderHierarchyOptions,
  reviveHierarchyOption,
} from './hierarchyPropertyOptions'

interface IUseHierarchyOptionsEditorArgs {
  configuration: HierarchyConfiguration
  isCreateMode?: boolean
  usedOptionValues?: Set<string>
  isUsageUnknown?: boolean
  hasUsageError?: boolean
  // The option values are fixed - the location type list. Reordering among
  // siblings stays possible; moving an option to another parent does not.
  isFixedVocabulary?: boolean
  onConfigChange: (
    configUpdater: (prev: HierarchyConfiguration) => HierarchyConfiguration,
  ) => void
}

const EMPTY_USED_OPTION_VALUES: Set<string> = new Set()

const parseConfigurationOptions = (
  configuration: HierarchyConfiguration,
  newOptionValues: Set<string>,
  isCreateMode: boolean,
): HierarchyOption[] =>
  parseHierarchyOptions(configuration?.options).map(option => ({
    ...option,
    isNew: isCreateMode || newOptionValues.has(option.value),
  }))

export const useHierarchyOptionsEditor = ({
  configuration: propertyConfiguration,
  isCreateMode = false,
  usedOptionValues = EMPTY_USED_OPTION_VALUES,
  isUsageUnknown = false,
  hasUsageError = false,
  isFixedVocabulary = false,
  onConfigChange,
}: IUseHierarchyOptionsEditorArgs) => {
  const [newOptionValues, setNewOptionValues] = useState<Set<string>>(new Set())
  const [manuallyEditedValues, setManuallyEditedValues] = useState<Set<string>>(
    new Set(),
  )
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set())
  const [collapsedValues, setCollapsedValues] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [isBulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [restoredOptionLabels, setRestoredOptionLabels] = useState<string[]>([])
  const [pendingFocusValue, setPendingFocusValue] = useState<string | null>(
    null,
  )

  // The single home for the value-keyed invariant: every option value that is
  // renamed or freed must be re-keyed / pruned across ALL four tracking sets
  // together, never one at a time. Both coordinated mutations route through
  // this one applier so a set can never be forgotten.
  const applyToTrackingSets = useCallback(
    (updater: (set: Set<string>) => Set<string>) => {
      setNewOptionValues(updater)
      setManuallyEditedValues(updater)
      setSelectedValues(updater)
      setCollapsedValues(updater)
    },
    [],
  )

  const rekeyTrackedValue = useCallback(
    (oldValue: string, newValue: string) =>
      applyToTrackingSets(prev => renameValueInSet(prev, oldValue, newValue)),
    [applyToTrackingSets],
  )

  const freeTrackedValues = useCallback(
    (values: string[]) =>
      applyToTrackingSets(prev => pruneValuesFromSet(prev, values)),
    [applyToTrackingSets],
  )

  const options = useMemo(
    () =>
      parseConfigurationOptions(
        propertyConfiguration,
        newOptionValues,
        isCreateMode,
      ),
    [propertyConfiguration, newOptionValues, isCreateMode],
  )

  const optionValues = useMemo(
    () => new Set(options.filter(option => !option.deleted).map(o => o.value)),
    [options],
  )

  const expandedValues = useMemo(
    () =>
      new Set(
        options
          .filter(
            option => !option.deleted && !collapsedValues.has(option.value),
          )
          .map(option => option.value),
      ),
    [options, collapsedValues],
  )

  const parentValues = useMemo(() => getParentValues(options), [options])

  const areAllExpanded = useMemo(
    () => ![...parentValues].some(value => collapsedValues.has(value)),
    [parentValues, collapsedValues],
  )

  const rows = useMemo(
    () => flattenHierarchyRows(options, expandedValues, searchTerm),
    [options, expandedValues, searchTerm],
  )

  const searchMatchedValues = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    return options
      .filter(option => !option.deleted && optionMatchesQuery(option, query))
      .map(option => option.value)
  }, [options, searchTerm])

  const activeSelectedValues = useMemo(
    () => new Set([...selectedValues].filter(value => optionValues.has(value))),
    [selectedValues, optionValues],
  )

  const selectedSearchMatchCount = searchMatchedValues.filter(value =>
    activeSelectedValues.has(value),
  ).length

  const updateOptions = (newOptions: HierarchyOption[]): boolean => {
    if (
      isBlockedNestingConversion(
        options,
        newOptions,
        usedOptionValues,
        isUsageUnknown || hasUsageError,
      )
    ) {
      toast.error(
        hasUsageError
          ? Localization.translator.failedToLoadUsageData
          : isUsageUnknown
            ? Localization.translator.cantChangeNestingChecking
            : Localization.translator.optionUsedCannotNest,
      )
      return false
    }

    onConfigChange(prev => ({
      ...prev,
      options: newOptions.map(({ value, label, parentValue, deleted }) => ({
        value,
        label,
        ...(parentValue && { parentValue }),
        ...(deleted && { deleted }),
      })),
    }))
    return true
  }

  const expandValues = (values: string[]) => {
    setCollapsedValues(prev => pruneValuesFromSet(prev, values))
  }

  const toggleExpanded = (value: string) => {
    setCollapsedValues(prev => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }

  const toggleAllExpanded = () => {
    setCollapsedValues(areAllExpanded ? new Set(parentValues) : new Set())
  }

  const setSelected = (value: string, checked: boolean) => {
    setSelectedValues(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(value)
      } else {
        next.delete(value)
      }
      return next
    })
  }

  const setSelectAll = (checked: boolean) => {
    setSelectedValues(
      checked
        ? new Set([...activeSelectedValues, ...searchMatchedValues])
        : new Set(
            [...activeSelectedValues].filter(
              value => !searchMatchedValues.includes(value),
            ),
          ),
    )
  }

  const dismissRestoredOptions = () => setRestoredOptionLabels([])

  // Reviving merges the just-renamed new option into the archived one: drop the
  // new option, flip the archived option back to live, and move any children of
  // the new option onto it. Routed through updateOptions so the #2 leaf->parent
  // guard still applies (reviving a record-used leaf with children is blocked).
  const restoreAndApply = (
    newOptionIndex: number,
    deletedOption: HierarchyOption,
  ) => {
    const newOption = options[newOptionIndex]
    const revived = reviveHierarchyOption(
      options,
      newOptionIndex,
      deletedOption,
    )
    if (!revived) {
      toast.error(Localization.translator.cantRestoreExceedsDepth)
      return
    }

    if (!updateOptions(revived)) return

    setRestoredOptionLabels([deletedOption.label])
    freeTrackedValues([newOption.value])
  }

  const getNameDraftValidation = useCallback(
    (index: number, draft: string): HierarchyNameDraftValidation => {
      const normalized = normalizePropertyName(draft)

      const liveCollision = options.find(
        (currentOption, currentIndex) =>
          currentIndex !== index &&
          !currentOption.deleted &&
          currentOption.value === normalized,
      )
      const archivedCollision = options.find(
        (currentOption, currentIndex) =>
          currentIndex !== index &&
          !!currentOption.deleted &&
          currentOption.value === normalized,
      )

      const existingValues = otherOptionValues(options, index)

      return {
        normalized,
        liveCollisionLabel: liveCollision?.label ?? null,
        archivedCollisionLabel: archivedCollision?.label ?? null,
        archivedCollisionOption: archivedCollision ?? null,
        dedupedValue: makeUniquePropertyName(normalized, existingValues),
      }
    },
    [options],
  )

  const updateOption = (
    index: number,
    changes: Partial<Pick<HierarchyOption, 'label' | 'value'>>,
  ) => {
    const option = options[index]
    if (!option) return

    if (changes.value !== undefined) {
      if (!option.isNew) return

      const collision =
        !!changes.value.trim() &&
        options.find(
          (currentOption, currentIndex) =>
            currentIndex !== index && currentOption.value === changes.value,
        )

      if (!changes.value.trim() || collision) return
    }

    const nextValue = changes.value ?? option.value

    const updatedOptions = options.map((currentOption, currentIndex) => {
      if (currentIndex === index) {
        return { ...currentOption, ...changes, value: nextValue }
      }

      if (currentOption.parentValue === option.value) {
        return { ...currentOption, parentValue: nextValue }
      }

      return currentOption
    })

    if (!updateOptions(updatedOptions)) return

    if (nextValue !== option.value) {
      rekeyTrackedValue(option.value, nextValue)
    }
  }

  const markNameManuallyEdited = (value: string) => {
    setManuallyEditedValues(prev => new Set(prev).add(value))
  }

  const handleLabelBlur = (index: number) => {
    const option = options[index]
    if (!option || !option.isNew || manuallyEditedValues.has(option.value)) {
      return
    }

    const baseValue = normalizePropertyName(option.label)
    if (!baseValue) return

    const existingValues = otherOptionValues(options, index)
    const uniqueValue = makeUniquePropertyName(baseValue, existingValues)
    if (uniqueValue !== option.value) {
      updateOption(index, { value: uniqueValue })
    }
  }

  const makeNewOptionLabel = () => {
    let label = Localization.translator.newOption
    let counter = 1

    while (options.some(option => option.label === label && !option.deleted)) {
      counter++
      label = `${Localization.translator.newOption} ${counter}`
    }

    return label
  }

  const createNewOption = (parentValue?: string): HierarchyOption => {
    const label = makeNewOptionLabel()
    return {
      value: createUniqueHierarchyOptionValue(label, options),
      label,
      parentValue,
      isNew: true,
    }
  }

  const commitNewOption = (
    nextOptions: HierarchyOption[],
    value: string,
  ): void => {
    if (!updateOptions(nextOptions)) return
    setNewOptionValues(prev => new Set(prev).add(value))
    setPendingFocusValue(value)
  }

  const onAutoFocusHandled = useCallback(() => setPendingFocusValue(null), [])

  const addOption = () => {
    const option = createNewOption()
    commitNewOption([...options, option], option.value)
  }

  const insertOptionBelow = (targetValue: string) => {
    const targetIndex = options.findIndex(
      option => option.value === targetValue,
    )
    if (targetIndex < 0) return

    const option = createNewOption(options[targetIndex].parentValue)

    // The new option shares the target's parentValue, so flattenHierarchyRows
    // renders it as the target's sibling and sibling order follows array
    // position — inserting right after the target makes it the next sibling.
    // The target's descendants render beneath it via parentValue wherever they
    // sit in this flat array, so no subtree scan is needed.
    const insertIndex = targetIndex + 1

    commitNewOption(
      [...options.slice(0, insertIndex), option, ...options.slice(insertIndex)],
      option.value,
    )
  }

  const indentSelected = () => {
    if (!activeSelectedValues.size) return

    const { options: updatedOptions, parentValuesToExpand } =
      indentHierarchyOptions(options, activeSelectedValues, rows)

    if (updateOptions(updatedOptions)) {
      expandValues(parentValuesToExpand)
    }
  }

  const outdentSelected = () => {
    if (!activeSelectedValues.size) return

    updateOptions(outdentHierarchyOptions(options, activeSelectedValues))
  }

  const deleteSelected = () => {
    if (!activeSelectedValues.size) return

    const { options: updatedOptions, freedValues } = deleteHierarchyOptions(
      options,
      activeSelectedValues,
    )

    if (!updateOptions(updatedOptions)) return

    setSelectedValues(new Set())

    if (freedValues.length) {
      freeTrackedValues(freedValues)
    }
  }

  const reorder = (oldIndex: number, newIndex: number) => {
    const result = reorderHierarchyOptions({
      options,
      expandedValues,
      searchTerm,
      oldIndex,
      newIndex,
    })
    if (!result) return
    if (isFixedVocabulary && changesHierarchyParents(options, result.options)) {
      return
    }

    if (updateOptions(result.options) && result.parentValueToExpand) {
      expandValues([result.parentValueToExpand])
    }
  }

  const bulkAdd = () => {
    const { options: parsedOptions, revivedValues } = parseBulkHierarchyOptions(
      bulkText,
      options,
      MAX_HIERARCHY_DEPTH,
    )
    if (!parsedOptions.length && !revivedValues.size) return

    const nextOptions = options.map(option =>
      revivedValues.has(option.value) ? { ...option, deleted: false } : option,
    )

    if (!updateOptions([...nextOptions, ...parsedOptions])) return

    setNewOptionValues(prev => {
      const next = new Set(prev)
      parsedOptions.forEach(option => next.add(option.value))
      return next
    })
    expandValues(
      parsedOptions
        .map(option => option.parentValue)
        .filter((value): value is string => Boolean(value)),
    )
    if (revivedValues.size) {
      setRestoredOptionLabels(
        options
          .filter(option => revivedValues.has(option.value))
          .map(option => option.label),
      )
    }
    setBulkText('')
    setBulkDialogOpen(false)
  }

  return {
    options,
    optionValues,
    rows,
    parentValues,
    expandedValues,
    areAllExpanded,
    searchMatchedValues,
    selectedSearchMatchCount,
    activeSelectedValues,
    selectedCount: activeSelectedValues.size,
    newOptionValues,
    manuallyEditedValues,
    selectedValues,
    collapsedValues,
    searchTerm,
    setSearchTerm,
    bulkText,
    setBulkText,
    isBulkDialogOpen,
    setBulkDialogOpen,
    restoredOptionLabels,
    dismissRestoredOptions,
    pendingFocusValue,
    onAutoFocusHandled,
    getNameDraftValidation,
    addOption,
    insertOptionBelow,
    updateOption,
    markNameManuallyEdited,
    handleLabelBlur,
    restoreAndApply,
    setSelected,
    setSelectAll,
    toggleExpanded,
    toggleAllExpanded,
    indentSelected,
    outdentSelected,
    deleteSelected,
    reorder,
    bulkAdd,
  }
}
